import { useState } from "react";
import {
  Link as RouterLink,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  List,
  ListItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import {
  formatTournamentDateRange,
  formatTournamentFacilityLine,
  getTournamentCommerceLabel,
} from "@bw/shared/utils";
import type {
  Schema,
  TournamentResponse,
  TournamentEventResponse,
} from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { DetailHeader, ShareButton } from "../../../common/components/DetailHeader";
import { useTournamentDetail } from "../hooks/useTournamentDetail";
import { useTournamentArchive } from "../hooks/useTournamentArchive";
import { useTournamentArchiveRelease } from "../hooks/useTournamentArchiveRelease";
import { useMyTournamentArchiveCertificates } from "../hooks/useMyTournamentArchiveCertificates";
import { useTournamentEvents } from "../hooks/useTournamentEvents";
import { useTournamentRegistrations } from "../hooks/useTournamentRegistrations";
import { TournamentDrawsSection } from "../components/TournamentDrawsSection";
import { TournamentEventList } from "../components/TournamentEventList";
import { TournamentLiveTab } from "../components/TournamentLiveTab";
import { TournamentPublicLiveTab } from "../components/TournamentPublicLiveTab";
import { TournamentOverviewPanel } from "../components/TournamentOverviewPanel";
import { OfficialBadge, OfficialRatingNote } from "../components/OfficialBadge";
import { TournamentPlayersSection } from "../components/TournamentPlayersSection";
import { tournamentApi, tournamentDayApi } from "@bw/shared/api";
import {
  useAuth,
  useMyTournamentLive,
  useMyTournamentLiveSchedule,
  usePublicTournamentLive,
  usePublicTournamentLiveSchedule,
} from "@bw/shared/hooks";
import { readSearchReturn } from "../../../common/searchNavigation";

/**
 * TournamentDetailPage (/tournaments/:tournamentId) — the player's READ-ONLY
 * tournament detail, reached THROUGH a browse card. Reads the PUBLIC detail (GET
 * /tournaments/{id} → TournamentResponse, anonymous-readable) and renders ONLY
 * the player-relevant fields: identity, key dates, venue/travel, schedule,
 * registration and fees, match format, entry requirements, draws/live state,
 * refund terms, legal notes, and the PUBLIC organizer contact. Operator-only
 * controls and identity keys never enter this surface.
 *
 * The registration open/closed state is read from the BACKEND's authoritative
 * `lifecycle.registration_state` — the frontend NEVER re-derives eligibility from
 * the raw registration_*_at timestamps (the backend forces "closed" for
 * completed / archived / cancelled events even when the close timestamp is still
 * in the future; a date-only guess would tell a player entries are open when
 * registration will be rejected).
 *
 * FRONTEND-HONESTY (#1398): TournamentResponse ALSO carries operator-internal
 * fields — `created_by` (a uid), `staff[].user_id` / `staff[].contact_email`
 * (operator channels), and operational `stats` counts — that this public detail
 * MUST NOT render. They are simply never read here; no uuid-shaped value leaks
 * into the DOM (facility_id / tournament_id are values / link targets, not text).
 *
 * A 404 / fetch error → an HONEST "Tournament not found" / error state, never a
 * blank page. Loading is role="status"; an absent fee shows an honest placeholder
 * (the commerce helper renders "Events coming soon"), never a fabricated "$0".
 */

/**
 * Player-facing registration-state phrasing, mapped from the BACKEND's
 * authoritative `lifecycle.registration_state` ("scheduled" | "open" | "closed").
 * When `lifecycle` is absent we do NOT fabricate an open/closed state — a neutral
 * honest placeholder is shown instead.
 */
const REGISTRATION_STATE_LABELS: Record<string, string> = {
  open: "Entries open",
  scheduled: "Entries soon",
  closed: "Entries closed",
};
const REGISTRATION_STATE_FALLBACK = "Entry details to be announced";
type TournamentParticipantLiveSnapshot =
  Schema["TournamentParticipantLiveSnapshot"];
type TournamentPublicLiveSnapshot = Schema["TournamentPublicLiveSnapshot"];

/**
 * Entry statuses that mean the player no longer holds a place — used only to
 * decide the standalone refund-section dedup. Mirrors the same set in
 * TournamentEventRow / TournamentEventList so the page and the rows agree on what
 * "still entered" means.
 */
const INACTIVE_ENTRY_STATUSES = new Set([
  "withdrawn",
  "rejected",
  "cancelled",
  "declined",
]);

export function TournamentDetailPage() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const location = useLocation();
  const searchReturn = readSearchReturn(location.search);
  const { data, isLoading, isError } = useTournamentDetail(tournamentId);

  if (isLoading) {
    return (
      <Page>
        <Typography color="text.secondary" role="status">
          Loading tournament…
        </Typography>
      </Page>
    );
  }

  if (
    isError ||
    !data ||
    !tournamentId ||
    data.tournament_id !== tournamentId
  ) {
    return (
      <Page>
        <Alert severity="error" role="alert">
          We couldn&apos;t find this tournament. It may have been removed or the
          link is wrong.
        </Alert>
      </Page>
    );
  }

  return (
    <Page narrow>
      <TournamentDetailBody
        tournament={data}
        tournamentId={tournamentId}
        back={{
          to: searchReturn ?? "/compete/tournaments",
          label: searchReturn ? "Back to search" : "Back to tournaments",
        }}
      />
    </Page>
  );
}

/**
 * The browse facility metadata (city/state) is denormalized onto the detail
 * response at runtime but is not on the generated TournamentResponse schema, so
 * read it through a narrow optional accessor (never a derived value).
 */
type FacilityRegion = {
  facility_city?: string | null;
  facility_state?: string | null;
};

type PublicArchiveReceipt = Schema["TournamentPublicArchiveResponse"];
type PublicArchiveRelease = Schema["TournamentPublicArchiveReleaseResponse"];
type OwnArchiveCertificate = Schema["TournamentOwnArchiveCertificateResponse"];

function isUuid(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value,
    )
  );
}

function isDateTime(value: unknown): value is string {
  return typeof value === "string" && !Number.isNaN(Date.parse(value));
}

function normalizePublicArchiveRelease(
  value: PublicArchiveRelease | undefined,
  tournamentId: string,
  archive: PublicArchiveReceipt | null,
): PublicArchiveRelease | null {
  if (
    !value ||
    !archive ||
    value.tournament_id !== tournamentId ||
    value.archive_id !== archive.archive_id ||
    !isUuid(value.release_id) ||
    !isDateTime(value.released_at) ||
    typeof value.report_available !== "boolean"
  ) {
    return null;
  }
  return value;
}

function isOwnedArchiveCertificate(
  value: OwnArchiveCertificate,
  tournamentId: string,
  archiveId: string,
): boolean {
  return (
    value.tournament_id === tournamentId &&
    value.archive_id === archiveId &&
    isUuid(value.certificate_id) &&
    typeof value.event_name === "string" &&
    value.event_name.trim().length > 0 &&
    typeof value.placement_label === "string" &&
    value.placement_label.trim().length > 0 &&
    typeof value.download_available === "boolean" &&
    isDateTime(value.issued_at)
  );
}

function saveDownload(download: { blob: Blob; filename: string }) {
  const objectUrl = URL.createObjectURL(download.blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = download.filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/**
 * The public detail route has already removed non-canonical prospectus
 * metadata. The browser keeps the action same-origin even when a stale client
 * cache or mocked response carries an unexpected HTTPS origin: Tournament
 * rechecks visibility and Media ownership before it redirects or streams bytes.
 */
function safeProspectusHref(
  tournamentId: string,
  value: string | null | undefined,
): string | null {
  const href = value?.trim();
  if (!href) return null;
  try {
    const parsed = new URL(href, window.location.origin);
    if (
      parsed.protocol === "https:" ||
      parsed.origin === window.location.origin
    ) {
      return `/bff/api/tournaments/${encodeURIComponent(tournamentId)}/prospectus`;
    }
  } catch {
    // No player-facing action is more honest than a broken document link.
  }
  return null;
}

function TournamentDetailBody({
  tournament,
  tournamentId,
  back,
}: {
  tournament: TournamentResponse;
  tournamentId: string;
  back: { to: string; label: string };
}) {
  const { user } = useAuth();
  const region = tournament as TournamentResponse & FacilityRegion;
  const archive = useTournamentArchive(
    tournamentId,
    tournament.status === "archived",
  );
  // The query key is tournament-scoped, but a stale/corrupt transport payload
  // must not make one event appear to carry another event's official release.
  const archiveReceipt =
    archive.data?.tournament_id === tournamentId ? archive.data : null;
  const archiveRelease = useTournamentArchiveRelease(
    tournamentId,
    archiveReceipt !== null,
  );
  const archiveReleaseReceipt = normalizePublicArchiveRelease(
    archiveRelease.data,
    tournamentId,
    archiveReceipt,
  );
  const myArchiveCertificates = useMyTournamentArchiveCertificates(
    tournamentId,
    archiveReleaseReceipt !== null,
  );
  const [archiveDownloadError, setArchiveDownloadError] = useState<
    "certificate" | "report" | null
  >(null);
  const [archiveDownloadPending, setArchiveDownloadPending] = useState<
    string | null
  >(null);
  // EPIC 1.2: the caller's entries SCOPED to this tournament — the page reads it to
  // gate the multi-event register surface AND to decide the standalone refund
  // section (a registered player sees the refund policy in the withdraw confirm,
  // so the standalone copy is for the not-yet-registered browse context). AUTH-
  // gated + SERVER-SCOPED; an anonymous viewer's disabled query → no entries.
  const myRegistrations = useTournamentRegistrations(tournamentId);
  const myEntries = myRegistrations.data ?? [];
  // The tournament's events — fetched ONCE here and handed to the single Events
  // surface (TournamentEventList) as both the catalog rows AND the fetch state
  // (slice (b) merge folded the read-only catalog into the actionable list, so the
  // list now owns the events-fetch loading/error/empty UX). Its OWN react-query
  // hook, isolated from the main detail: a failed events fetch surfaces the list's
  // alert without blanking the header/schedule.
  const eventsQ = useTournamentEvents(tournamentId);
  const facilityLine = formatTournamentFacilityLine(
    tournament.facility_name ?? "",
    region.facility_city,
    region.facility_state,
  );
  const dateRange = formatTournamentDateRange(
    tournament.start_date,
    tournament.end_date,
  );
  // Registration open/closed comes from the BACKEND's authoritative
  // lifecycle.registration_state — never re-derived from the raw timestamps.
  const registrationState = tournament.lifecycle?.registration_state;
  const registrationLabel = registrationState
    ? (REGISTRATION_STATE_LABELS[registrationState] ??
      REGISTRATION_STATE_FALLBACK)
    : REGISTRATION_STATE_FALLBACK;
  const commerceLabel = getTournamentCommerceLabel(
    tournament.registration_commerce,
  );
  // An ACTIVE entry (not withdrawn/rejected/cancelled) means the player currently
  // holds a place in this tournament — used only for the standalone refund-section
  // dedup (the per-event list owns the status + withdraw UX itself).
  const hasActiveEntry = myEntries.some(
    (e) => !INACTIVE_ENTRY_STATUSES.has(e.status),
  );
  const myEntryIds = new Set(
    myEntries
      .filter((e) => !INACTIVE_ENTRY_STATUSES.has(e.status))
      .map((e) => e.entry_id),
  );
  // After slice (b) the single Events surface (TournamentEventList) ALWAYS renders
  // — it is the merged catalog + register list, so it must show the events as a
  // read-only browse even when registration is closed (never a blank Events tab).
  // `registrationOpen` (the BACKEND's authoritative state) decides the per-row
  // affordance: OPEN → actionable Register/Enter-solo; CLOSED → read-only rows
  // (display facts only). A player who already holds an entry still sees their
  // status + Withdraw on that row regardless (the row owns that, unconditionally),
  // so registration closing never hides a held place — never a dead Register
  // either (a closed not-entered row has no button at all).
  const registrationOpen = registrationState === "open";
  // The refund policy is surfaced in the withdraw confirm for a player who holds an
  // entry, so the standalone "Refund policy" section is for the browse /
  // not-yet-registered context — a registered player would see a redundant copy.
  const isRegistered = hasActiveEntry;
  const prospectusHref = safeProspectusHref(
    tournamentId,
    tournament.prospectus_pdf_url,
  );
  const archiveCertificates = (myArchiveCertificates.data ?? []).filter(
    (certificate) =>
      archiveReceipt !== null &&
      isOwnedArchiveCertificate(
        certificate,
        tournamentId,
        archiveReceipt.archive_id,
      ),
  );

  const downloadArchiveReport = async () => {
    if (!archiveReleaseReceipt || archiveDownloadPending !== null) return;
    setArchiveDownloadPending("report");
    setArchiveDownloadError(null);
    try {
      saveDownload(
        await tournamentApi.downloadPublicArchiveReport(tournamentId),
      );
    } catch {
      setArchiveDownloadError("report");
    } finally {
      setArchiveDownloadPending(null);
    }
  };

  const downloadArchiveCertificate = async (certificateId: string) => {
    if (!archiveReleaseReceipt || archiveDownloadPending !== null) return;
    setArchiveDownloadPending(certificateId);
    setArchiveDownloadError(null);
    try {
      saveDownload(
        await tournamentApi.downloadMyArchiveCertificate(
          tournamentId,
          certificateId,
        ),
      );
    } catch {
      setArchiveDownloadError("certificate");
    } finally {
      setArchiveDownloadPending(null);
    }
  };

  const infoSections = (
    <Stack spacing={3}>
      {/* ---- ABOUT (the Info segment) ----
          The description, venue, schedule, the authoritative registration state,
          and the fee summary, then the tournament-wide documents and contacts. */}
      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, md: 3 },
          gridTemplateColumns: tournament.poster_image_url
            ? { xs: "1fr", md: "minmax(220px, 300px) minmax(0, 1fr)" }
            : "1fr",
        }}
      >
        {tournament.poster_image_url ? (
          <Paper
            aria-label="Tournament poster"
            component="section"
            variant="outlined"
            sx={{ alignSelf: "start", overflow: "hidden", p: 0.75 }}
          >
            <Box
              alt={`${tournament.name} poster`}
              component="img"
              src={tournament.poster_image_url}
              sx={{
                aspectRatio: "3 / 4",
                borderRadius: 1,
                display: "block",
                objectFit: "cover",
                width: "100%",
              }}
            />
          </Paper>
        ) : null}
        <Stack spacing={2}>
          {tournament.description ? (
            <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
              {tournament.description}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            }}
          >
            <Paper
              component="section"
              variant="outlined"
              aria-label="When and where"
              sx={{ p: 2 }}
            >
              <Typography variant="overline" color="text.secondary">
                When and where
              </Typography>
              {tournament.venue_name || facilityLine ? (
                <Typography sx={{ fontWeight: 800 }}>
                  {tournament.venue_name ?? facilityLine}
                </Typography>
              ) : null}
              {tournament.venue_address ? (
                <Typography color="text.secondary" variant="body2">
                  {tournament.venue_address}
                </Typography>
              ) : null}
              <Typography color="text.secondary">{dateRange}</Typography>
            </Paper>

            <Paper
              component="section"
              variant="outlined"
              aria-label="Registration and entry"
              sx={{ p: 2 }}
            >
              <Typography variant="overline" color="text.secondary">
                Registration and entry
              </Typography>
              <Box sx={{ mb: 0.75 }}>
                <Chip
                  size="small"
                  color={registrationOpen ? "primary" : "default"}
                  label={registrationLabel}
                />
              </Box>
              <Typography color="text.secondary">{commerceLabel}</Typography>
              {tournament.lifecycle?.next_milestone_label ? (
                <Typography
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                  variant="body2"
                >
                  Next: {tournament.lifecycle.next_milestone_label}
                </Typography>
              ) : null}
            </Paper>
          </Box>
        </Stack>
      </Box>

      {prospectusHref ? (
        <Paper
          component="section"
          variant="outlined"
          aria-label="Prospectus"
          sx={{ p: 2 }}
        >
          <Typography component="h2" variant="h6">
            Prospectus
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            <Link
              href={prospectusHref}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ alignItems: "center", display: "inline-flex", gap: 0.75 }}
            >
              View prospectus
              <OpenInNewRoundedIcon fontSize="small" aria-hidden="true" />
            </Link>
            <Typography color="text.secondary" variant="body2">
              {tournament.prospectus_filename ?? "Tournament prospectus.pdf"}
            </Typography>
          </Stack>
        </Paper>
      ) : null}

      {tournament.status === "archived" ? (
        <Paper component="section" variant="outlined" sx={{ p: 2 }}>
          <Typography component="h2" variant="h6">
            Official archive
          </Typography>
          {archive.isLoading ? (
            <Typography color="text.secondary" role="status" sx={{ mt: 0.75 }}>
              Loading official archive…
            </Typography>
          ) : archiveReceipt ? (
            <Stack spacing={1} sx={{ mt: 0.75 }}>
              <Typography color="text.secondary">
                Archive receipt published{" "}
                {new Date(archiveReceipt.archived_at).toLocaleDateString(
                  "en-US",
                  {
                    dateStyle: "medium",
                  },
                )}
                .
              </Typography>
              {archiveReleaseReceipt?.report_available ? (
                <Box>
                  <Button
                    disabled={archiveDownloadPending !== null}
                    onClick={() => void downloadArchiveReport()}
                    size="small"
                    variant="outlined"
                  >
                    {archiveDownloadPending === "report"
                      ? "Preparing report…"
                      : "Download official report"}
                  </Button>
                </Box>
              ) : null}
              {archiveDownloadError === "report" ? (
                <Alert severity="error">
                  Couldn&apos;t download the official report. Try again.
                </Alert>
              ) : null}
              {user && archiveReleaseReceipt ? (
                <Stack component="section" spacing={1} sx={{ pt: 0.5 }}>
                  <Typography component="h3" variant="subtitle1">
                    Your certificates
                  </Typography>
                  {myArchiveCertificates.isLoading ? (
                    <Typography
                      color="text.secondary"
                      role="status"
                      variant="body2"
                    >
                      Loading your certificates…
                    </Typography>
                  ) : myArchiveCertificates.isError ? (
                    <Alert severity="error">
                      Couldn&apos;t load your certificates. Try again.
                    </Alert>
                  ) : archiveCertificates.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No certificate has been issued to you for this tournament.
                    </Typography>
                  ) : (
                    <List disablePadding>
                      {archiveCertificates.map((certificate) => (
                        <ListItem
                          disableGutters
                          divider
                          key={certificate.certificate_id}
                          secondaryAction={
                            certificate.download_available ? (
                              <Button
                                aria-label={`Download certificate for ${certificate.event_name}`}
                                disabled={archiveDownloadPending !== null}
                                onClick={() =>
                                  void downloadArchiveCertificate(
                                    certificate.certificate_id,
                                  )
                                }
                                size="small"
                                variant="outlined"
                              >
                                {archiveDownloadPending ===
                                certificate.certificate_id
                                  ? "Preparing…"
                                  : "Download"}
                              </Button>
                            ) : null
                          }
                        >
                          <Stack spacing={0.25} sx={{ pr: 14 }}>
                            <Typography variant="body2">
                              {certificate.event_name} ·{" "}
                              {certificate.placement_label}
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="caption"
                            >
                              Issued{" "}
                              {new Date(
                                certificate.issued_at,
                              ).toLocaleDateString("en-US", {
                                dateStyle: "medium",
                              })}
                            </Typography>
                          </Stack>
                        </ListItem>
                      ))}
                    </List>
                  )}
                  {archiveDownloadError === "certificate" ? (
                    <Alert severity="error">
                      Couldn&apos;t download your certificate. Try again.
                    </Alert>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              An official archive is not available for this historical
              tournament.
            </Typography>
          )}
        </Paper>
      ) : null}

      {/* ---- TOURNAMENT-LEVEL sections (always visible, below the tab strip) ----
          Refund policy / legal notes / organizer are tournament-wide, not specific
          to a read surface, so they sit below the tabs and render on either tab. */}
      {tournament.refund_policy_summary && !isRegistered ? (
        <Paper
          component="section"
          variant="outlined"
          aria-label="Refund policy"
          sx={{ p: 2 }}
        >
          <Typography component="h2" variant="h6">
            Refund policy
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {tournament.refund_policy_summary}
          </Typography>
        </Paper>
      ) : null}

      {tournament.legal_notes ? (
        <Paper
          component="section"
          variant="outlined"
          aria-label="Legal notes"
          sx={{ p: 2 }}
        >
          <Typography component="h2" variant="h6">
            Legal notes
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {tournament.legal_notes}
          </Typography>
        </Paper>
      ) : null}

      <OrganizerContact tournament={tournament} />
    </Stack>
  );

  const closesAt = tournament.registration_close_at;
  const closesLine = [
    registrationOpen && closesAt && !Number.isNaN(Date.parse(closesAt))
      ? `Closes ${new Date(closesAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : null,
    // Whole-dollar prices read "$15", not "$15.00".
    commerceLabel.replace(/([0-9])\.00(?![0-9])/g, "$1"),
  ]
    .filter(Boolean)
    .join(" · ");
  const signedDocuments = (tournament.legal_documents ?? []).filter(
    (document) => document.requires_signature,
  );

  return (
    <Stack spacing={1.5}>
      <DetailHeader
        title={tournament.name}
        subtitle={[dateRange, tournament.venue_name ?? facilityLine]
          .filter(Boolean)
          .join(" · ")}
        back={back}
        actions={<ShareButton title={tournament.name} />}
      />
      {/* ---- TAB SHELL: Events, Draws, Schedule, Results, Entrants and Info as one
          segmented tab control. The Events panel renders ONE list (which owns the
          events-fetch loading/error/empty UX). Draws and Results mount ONLY when
          their tab is active so their hooks fire on activation, not on load. */}
      <TournamentReadTabs
        tournament={tournament}
        tournamentType={tournament.tournament_type}
        registrationOpen={registrationOpen}
        eventsItems={eventsQ.data?.items ?? []}
        eventsLoading={eventsQ.isLoading}
        eventsError={eventsQ.isError}
        tournamentId={tournamentId}
        facilityId={tournament.facility_id}
        refundPolicySummary={tournament.refund_policy_summary}
        legalDocuments={tournament.legal_documents ?? []}
        legalNotes={tournament.legal_notes}
        requiresForeignDeclaration={
          tournament.compliance_requirements.require_foreign_country_declaration
        }
        authenticated={Boolean(user)}
        actorIdentity={user?.uid ?? null}
        myEntryIds={myEntryIds}
        info={infoSections}
        eventsLead={
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", minWidth: 0 }}
            >
              {tournament.is_official ? <OfficialBadge /> : null}
              <Chip
                size="small"
                color={registrationOpen ? "primary" : "default"}
                label={registrationLabel}
                sx={{ fontWeight: 700, flexShrink: 0 }}
              />
              <Typography noWrap variant="body2" color="text.secondary">
                {closesLine}
              </Typography>
            </Stack>
            {tournament.is_official ? <OfficialRatingNote /> : null}
            {signedDocuments.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ alignItems: "center", color: "text.secondary" }}
              >
                <InfoRoundedIcon sx={{ fontSize: 16 }} aria-hidden />
                <Typography variant="body2">
                  {signedDocuments.length === 1
                    ? `${signedDocuments[0].title} required once before entering`
                    : `${signedDocuments.length} documents to accept before entering`}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        }
      />
    </Stack>
  );
}

/**
 * The Tournamentsoftware-style tab shell gates **Events** (default-selected),
 * **Draws**, **Schedule**, **Results**, **Entrants** and **Info**. The schedule is public for every viewer; an authenticated
 * participant receives the private projection, while a denied authenticated
 * spectator falls back to the same public projection as an anonymous viewer.
 * The shell remains presentational — it threads server-owned facts and never
 * derives eligibility, registration, money, score, or schedule state.
 *
 * ARIA (APG horizontal-tabs, automatic activation — selection follows focus):
 *   - role="tablist" (aria-label "Tournament views"), class .compete-tabs (reused
 *     from the existing compete/social tabs so a :root restyle recolours it too).
 *   - six role="tab" buttons (Events / Draws / Schedule / Results / Entrants / Info), each declaring
 *     aria-selected + aria-controls → the panel id, class .compete-tab
 *     (+ .compete-tab--active on the selected one).
 *   - ONE role="tabpanel" whose id === the active tab's aria-controls and whose
 *     aria-labelledby === the active tab's id.
 *   - KEYBOARD ROVING the planner asked to ADD (the existing compete/social tabs
 *     lack it): ArrowRight/ArrowLeft move selection WITH WRAP, Home → first,
 *     End → last; selection follows focus and DOM focus moves to the new tab.
 *     Roving tabindex — the selected tab is tabIndex 0, the other -1.
 *
 * The Draws section is mounted ONLY while its tab is active, so its
 * listDraws/standings/bracket fetches fire on activation (not on page load) —
 * the draws tests now expect this.
 */
const READ_TABS = [
  "events",
  "draws",
  "live",
  "results",
  "players",
  "overview",
] as const;
type ReadTab = (typeof READ_TABS)[number];

/** Approved segment names; the URL keeps the stable `tab=` values. */
const TAB_LABEL: Record<ReadTab, string> = {
  events: "Events",
  draws: "Draws",
  live: "Schedule",
  results: "Results",
  players: "Entrants",
  overview: "Info",
};
const PANEL_ID: Record<ReadTab, string> = {
  overview: "panel-overview",
  events: "panel-events",
  players: "panel-players",
  draws: "panel-draws",
  live: "panel-live",
  results: "panel-results",
};
const TAB_ID: Record<ReadTab, string> = {
  overview: "tab-overview",
  events: "tab-events",
  players: "tab-players",
  draws: "tab-draws",
  live: "tab-live",
  results: "tab-results",
};

function TournamentReadTabs({
  tournament,
  tournamentType,
  registrationOpen,
  eventsItems,
  eventsLoading,
  eventsError,
  tournamentId,
  facilityId,
  refundPolicySummary,
  legalDocuments,
  legalNotes,
  requiresForeignDeclaration,
  authenticated,
  actorIdentity,
  myEntryIds,
  info,
  eventsLead,
}: {
  tournament: TournamentResponse;
  tournamentType: string;
  /** The BACKEND's authoritative registration-open state, threaded to the single
   *  Events list → each row, deciding actionable vs read-only browse (slice (b)). */
  registrationOpen: boolean;
  eventsItems: TournamentEventResponse[];
  /** The page's events-fetch state, threaded into the single Events list so it
   *  owns the events-fetch loading/error UX (slice (b) merge). */
  eventsLoading: boolean;
  eventsError: boolean;
  tournamentId: string;
  facilityId: string;
  refundPolicySummary: string | null | undefined;
  legalDocuments: NonNullable<TournamentResponse["legal_documents"]>;
  legalNotes: string | null | undefined;
  requiresForeignDeclaration: boolean;
  authenticated: boolean;
  actorIdentity: string | null;
  /** The viewer's active entry ids — their own matches are outlined. */
  myEntryIds: ReadonlySet<string>;
  /** The Info segment: description, venue, documents and contacts. */
  info: React.ReactNode;
  /** Status, deadline and price range, shown once at the top of Events. */
  eventsLead: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tournamentInProgress =
    tournament.effective_status === "in_progress" ||
    tournament.status === "in_progress";
  const defaultTab: ReadTab = tournamentInProgress
    ? "live"
    : registrationOpen
      ? "events"
      : "overview";
  const active: ReadTab =
    requested === "details"
      ? "overview"
      : (READ_TABS.find((tab) => tab === requested) ?? defaultTab);
  const live = useMyTournamentLive(tournamentId, {
    enabled: authenticated && active === "live",
    authGeneration: actorIdentity ?? "anonymous",
  });
  const liveSnapshot = live.data as
    | TournamentParticipantLiveSnapshot
    | undefined;
  const liveSchedule = useMyTournamentLiveSchedule(tournamentId, {
    enabled: authenticated && active === "live",
    authGeneration: actorIdentity ?? "anonymous",
    revision: liveSnapshot?.revision,
  });
  const privateErrorStatus = (live.error as { status?: number } | null)?.status;
  const privateDenied =
    authenticated &&
    live.isError &&
    (privateErrorStatus === 403 || privateErrorStatus === 404);
  const publicLiveEnabled =
    active === "live" && (!authenticated || privateDenied);
  const publicLive = usePublicTournamentLive(tournamentId, {
    enabled: publicLiveEnabled,
  });
  const publicSnapshot = publicLive.data as
    | TournamentPublicLiveSnapshot
    | undefined;
  const publicSchedule = usePublicTournamentLiveSchedule(tournamentId, {
    enabled: publicLiveEnabled,
    revision: publicSnapshot?.revision,
  });
  const checkIn = async () => {
    const revision = liveSnapshot?.own_presence?.revision ?? 0;
    await tournamentDayApi.selfCheckInTournament(tournamentId, {
      command: "self_check_in",
      idempotency_key: crypto.randomUUID(),
      expected_revision: revision,
    });
    await live.refetch();
  };
  return (
    <>
      {active === "events" ? eventsLead : null}
      <Tabs
        value={active}
        onChange={(_, value: ReadTab) => {
          const next = new URLSearchParams(searchParams);
          if (value === defaultTab) next.delete("tab");
          else next.set("tab", value);
          setSearchParams(next);
        }}
        aria-label="Tournament views"
        selectionFollowsFocus
        scrollButtons={false}
        variant="scrollable"
        slotProps={{ indicator: { sx: { display: "none" } } }}
        sx={{
          minHeight: 0,
          p: 0.5,
          borderRadius: "12px",
          bgcolor: "action.hover",
          "& .MuiTabs-flexContainer": { gap: 0.5 },
        }}
      >
        {READ_TABS.map((tab) => {
          return (
            <Tab
              key={tab}
              value={tab}
              id={TAB_ID[tab]}
              aria-controls={PANEL_ID[tab]}
              label={TAB_LABEL[tab]}
              disableRipple
              sx={{
                minHeight: 34,
                minWidth: 0,
                px: 1.5,
                py: 0.5,
                borderRadius: "9px",
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "text.primary",
                  fontWeight: 700,
                  bgcolor: "background.default",
                },
              }}
            />
          );
        })}
      </Tabs>

      <Box
        id={PANEL_ID[active]}
        role="tabpanel"
        aria-labelledby={TAB_ID[active]}
        tabIndex={0}
        sx={{ pt: 0.5, pb: 2, outline: "none" }}
      >
        {active === "overview" ? (
          <Stack spacing={3}>
            <TournamentOverviewPanel tournament={tournament} />
            {info}
          </Stack>
        ) : active === "results" ? (
          <TournamentDrawsSection
            tournamentId={tournamentId}
            tournamentType={tournamentType}
            myEntryIds={myEntryIds}
            mode="results"
          />
        ) : active === "events" ? (
          /* EPIC 1.2 + slice (b) MERGE — the SINGLE Events surface
             (TournamentEventList): ONE row per event with the signed-in player's
             per-event eligibility + LIVE status + Register / Registered+Withdraw
             (register for SEVERAL events independently), now ALSO carrying the
             catalog's format + counts DISPLAY lines on each row. It owns the
             per-event register (free + paid Stripe checkout) AND withdraw (the
             #1402 refund path) dialogs, plus — folded in from the deleted catalog —
             the events-fetch loading/error/empty UX (eventsLoading/eventsError
             threaded from the page's single useTournamentEvents query, so a failed
             events fetch surfaces THIS list's isolated alert without blanking the
             header above). ALWAYS rendered (it is the merged catalog → the Events
             tab is never blank): `registrationOpen` decides per-row affordance —
             OPEN → actionable, CLOSED → read-only browse. refund_policy_summary is
             threaded for the withdraw confirm's DISPLAY-ONLY policy text. */
          <TournamentEventList
            tournamentId={tournamentId}
            events={eventsItems}
            facilityId={facilityId}
            refundPolicySummary={refundPolicySummary}
            legalDocuments={legalDocuments}
            legalNotes={legalNotes}
            requiresForeignDeclaration={requiresForeignDeclaration}
            registrationOpen={registrationOpen}
            eventsLoading={eventsLoading}
            eventsError={eventsError}
          />
        ) : active === "players" ? (
          <TournamentPlayersSection tournamentId={tournamentId} />
        ) : active === "draws" ? (
          /* Draws + standings/bracket — its OWN react-query hook, isolated the
             same way: a failed draws fetch surfaces this section's alert without
             blanking the header / Events. Mounted ONLY while the Draws tab is
             active, so its fetches fire on activation, not on page load. */
          <TournamentDrawsSection
            tournamentId={tournamentId}
            tournamentType={tournamentType}
            myEntryIds={myEntryIds}
          />
        ) : authenticated && !privateDenied ? (
          <TournamentLiveTab
            snapshot={liveSnapshot ?? null}
            loading={live.isLoading}
            authorityError={live.isError}
            errorStatus={privateErrorStatus}
            onRetry={() => live.refetch()}
            onCheckIn={checkIn}
            schedule={{
              pages: liveSchedule.data?.pages ?? [],
              loading: liveSchedule.isLoading,
              error: liveSchedule.isError,
              hasNextPage: Boolean(liveSchedule.hasNextPage),
              loadingMore: liveSchedule.isFetchingNextPage,
              onLoadMore: () => void liveSchedule.fetchNextPage(),
              onRetry: () => void liveSchedule.refetch(),
            }}
          />
        ) : (
          <TournamentPublicLiveTab
            snapshot={publicSnapshot ?? null}
            loading={publicLive.isLoading}
            error={publicLive.isError}
            onRetry={() => publicLive.refetch()}
            schedule={{
              pages: publicSchedule.data?.pages ?? [],
              loading: publicSchedule.isLoading,
              error: publicSchedule.isError,
              hasNextPage: Boolean(publicSchedule.hasNextPage),
              loadingMore: publicSchedule.isFetchingNextPage,
              onLoadMore: () => void publicSchedule.fetchNextPage(),
              onRetry: () => void publicSchedule.refetch(),
            }}
          />
        )}
      </Box>
    </>
  );
}

/**
 * The PUBLIC organizer contact — email / phone / website. These are the
 * operator's *published* contact channels (player-relevant), distinct from the
 * operator-internal staff uids / created_by uid, which are never rendered.
 * Renders nothing when no public contact is set.
 */
function OrganizerContact({ tournament }: { tournament: TournamentResponse }) {
  const { organizer_email, organizer_phone, organizer_website } = tournament;
  if (!organizer_email && !organizer_phone && !organizer_website) {
    return null;
  }
  return (
    <Paper
      component="section"
      variant="outlined"
      aria-label="Organizer"
      sx={{ p: 2 }}
    >
      <Typography component="h2" variant="h6">
        Organizer
      </Typography>
      <List disablePadding sx={{ mt: 0.75 }}>
        {organizer_email ? (
          <ListItem disableGutters>
            <Link href={`mailto:${organizer_email}`}>{organizer_email}</Link>
          </ListItem>
        ) : null}
        {organizer_phone ? (
          <ListItem disableGutters>
            <Link href={`tel:${organizer_phone}`}>{organizer_phone}</Link>
          </ListItem>
        ) : null}
        {organizer_website ? (
          <ListItem disableGutters>
            {/* A friendly label, not the raw URL echoed verbatim (the href is
                the real destination). Cleaner than a long URL, and keeps the
                page's only registration-state text the one intended line. */}
            <Link href={organizer_website} target="_blank" rel="noreferrer">
              Event website
            </Link>
          </ListItem>
        ) : null}
      </List>
    </Paper>
  );
}
